import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import {
  Map, BookOpen, Lock, X, Bell, BellOff, Navigation2,
  ShieldCheck, Search, ChevronRight, Info,
} from "lucide-react";

// ─── DESIGN TOKENS — matches TrainingHub.jsx / CheatSheets.jsx / emails ────
const LIGHT_C = {
  page: "#f8fafc", surface: "#ffffff", surfaceAlt: "#f1f5f9",
  navy: "#0d1f3c", navyMid: "#1a3358",
  gold: "#c9971c", goldLight: "#fef9ec",
  green: "#059669", greenLight: "#dcfce7",
  blue: "#1e56a8", blueLight: "#eff6ff",
  amber: "#d97706", amberLight: "#fef3c7",
  red: "#dc2626", redLight: "#fef2f2",
  muted: "#64748b", mutedLight: "#f1f5f9",
  text: "#0d1f3c", textMid: "#334155", textSub: "#475569", textLight: "#64748b",
  border: "#e2e8f0", white: "#ffffff",
};
const DARK_C = {
  page: "#0d1f3c", surface: "#142a4d", surfaceAlt: "#1a3358",
  navy: "#0d1f3c", navyMid: "#1a3358",
  gold: "#fbbf24", goldLight: "rgba(251,191,36,0.12)",
  green: "#34d399", greenLight: "rgba(52,211,153,0.12)",
  blue: "#60a5fa", blueLight: "rgba(96,165,250,0.12)",
  amber: "#fbbf24", amberLight: "rgba(251,191,36,0.12)",
  red: "#fb7185", redLight: "rgba(251,113,133,0.12)",
  muted: "rgba(240,244,248,0.55)", mutedLight: "rgba(255,255,255,0.06)",
  text: "#f0f4f8", textMid: "rgba(240,244,248,0.85)", textSub: "rgba(240,244,248,0.65)", textLight: "rgba(240,244,248,0.50)",
  border: "rgba(255,255,255,0.10)", white: "#ffffff",
};

const STATE_FLAGS = {
  AL:"🌲", AR:"🌾", AZ:"🌵", CA:"🌉", CO:"🏔️", CT:"⚓", DE:"💎",
  FL:"🌴", GA:"🍑", HI:"🌺", ID:"🥔", IL:"🌽", IN:"🏎️", IA:"🌽",
  KS:"🌾", KY:"🏇", LA:"🎷", ME:"🦞", MD:"🦀", MA:"🦃", MI:"🚗",
  MN:"❄️", MS:"🎵", MO:"🏛️", MT:"🦌", NE:"🌾", NV:"🎰", NH:"🍁",
  NJ:"🗽", NM:"🌶️", NY:"🗽", NC:"⭐", ND:"🌻", OH:"✈️", OK:"🛢️",
  OR:"🌲", PA:"🔔", RI:"⚓", SC:"🌴", SD:"🏔️", TN:"🎵", TX:"⭐",
  UT:"⛷️", VT:"🍁", VA:"🏛️", WA:"🌲", WV:"⛏️", WI:"🧀", WY:"🦬",
};

const GROUP_META = {
  pevo:       { label: "14 PEVO",       sub: "Performance-Based",        color: "green" },
  eight:      { label: "8-State",       sub: "Uniform Requirements",     color: "blue" },
  standalone: { label: "Stand-Alone",   sub: "Independent Requirements", color: "amber" },
  remaining:  { label: "Remaining 21",  sub: "Verify Individually",      color: "muted" },
};

// Same 51-entry dataset (50 states + DC) used by the standalone Cheat Sheet page.
const STATES = [
  { code:"AL", name:"Alabama",        group:"pevo", width:"14'0\"", height:"14'0\"", ins:"$750K CSL", night:"Allowed",            cert:"PEVO Cert" },
  { code:"AR", name:"Arkansas",       group:"pevo", width:"14'0\"", height:"14'0\"", ins:"$750K CSL", night:"Allowed",            cert:"PEVO Cert" },
  { code:"CO", name:"Colorado",       group:"pevo", width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"w/ lighting",        cert:"PEVO Cert" },
  { code:"CT", name:"Connecticut",    group:"pevo", width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted",         cert:"PEVO Cert" },
  { code:"DE", name:"Delaware",       group:"pevo", width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"w/ lighting",        cert:"PEVO Cert" },
  { code:"FL", name:"Florida",        group:"pevo", width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",            cert:"PEVO + Def. Driving" },
  { code:"GA", name:"Georgia",        group:"pevo", width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"w/ lighting",        cert:"PEVO Cert" },
  { code:"KY", name:"Kentucky",       group:"pevo", width:"14'0\"", height:"14'0\"", ins:"$750K CSL", night:"w/ lighting",        cert:"PEVO Cert" },
  { code:"LA", name:"Louisiana",      group:"pevo", width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"Allowed",            cert:"PEVO Cert" },
  { code:"MI", name:"Michigan",       group:"pevo", width:"14'6\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted",         cert:"PEVO Cert" },
  { code:"MS", name:"Mississippi",    group:"pevo", width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"Allowed",            cert:"PEVO Cert" },
  { code:"NC", name:"North Carolina", group:"pevo", width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"w/ lighting",        cert:"PEVO Cert" },
  { code:"NM", name:"New Mexico",     group:"pevo", width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",            cert:"PEVO Cert" },
  { code:"SC", name:"South Carolina", group:"pevo", width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"Allowed",            cert:"PEVO Cert" },
  { code:"AZ", name:"Arizona",  group:"eight", width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
  { code:"ID", name:"Idaho",    group:"eight", width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"w/ lighting", cert:"No cert" },
  { code:"IA", name:"Iowa",     group:"eight", width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
  { code:"KS", name:"Kansas",   group:"eight", width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
  { code:"MO", name:"Missouri", group:"eight", width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
  { code:"NE", name:"Nebraska", group:"eight", width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
  { code:"NV", name:"Nevada",   group:"eight", width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
  { code:"UT", name:"Utah",     group:"eight", width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",     cert:"No cert" },
  { code:"CA", name:"California", group:"standalone", width:"14'0\"", height:"14'0\"", ins:"$750K CSL", night:"No Fri 3pm\u2013Mon 6am", cert:"CALTRANS Permit" },
  { code:"OR", name:"Oregon",     group:"standalone", width:"14'6\"", height:"15'6\"", ins:"$750K CSL", night:"Restricted",              cert:"Permit-specific" },
  { code:"TN", name:"Tennessee",  group:"standalone", width:"14'6\"", height:"15'0\"", ins:"$750K CSL", night:"w/ lighting",             cert:"No cert" },
  { code:"TX", name:"Texas",      group:"standalone", width:"16'0\"", height:"16'0\"", ins:"$750K CSL", night:"Allowed",                 cert:"TxDOT License" },
  { code:"WI", name:"Wisconsin",  group:"standalone", width:"14'6\"", height:"15'0\"", ins:"$750K CSL", night:"w/ lighting",             cert:"No cert" },
  { code:"WY", name:"Wyoming",    group:"standalone", width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",                 cert:"No cert" },
  { code:"AK", name:"Alaska",        group:"remaining", width:"14'0\"", height:"15'0\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
  { code:"DC", name:"Washington DC", group:"remaining", width:"13'0\"", height:"13'6\"", ins:"$1M CSL",   night:"Restricted", cert:"No cert" },
  { code:"HI", name:"Hawaii",        group:"remaining", width:"10'0\"", height:"14'0\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
  { code:"IL", name:"Illinois",      group:"remaining", width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
  { code:"IN", name:"Indiana",       group:"remaining", width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
  { code:"MD", name:"Maryland",      group:"remaining", width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted", cert:"No cert" },
  { code:"MA", name:"Massachusetts", group:"remaining", width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted", cert:"No cert" },
  { code:"ME", name:"Maine",         group:"remaining", width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"w/ lighting",cert:"No cert" },
  { code:"MN", name:"Minnesota",     group:"remaining", width:"14'6\"", height:"15'6\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
  { code:"MT", name:"Montana",       group:"remaining", width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
  { code:"ND", name:"North Dakota",  group:"remaining", width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
  { code:"OH", name:"Ohio",          group:"remaining", width:"14'6\"", height:"14'0\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
  { code:"OK", name:"Oklahoma",      group:"remaining", width:"14'6\"", height:"15'0\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
  { code:"PA", name:"Pennsylvania",  group:"remaining", width:"14'6\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted", cert:"No cert" },
  { code:"RI", name:"Rhode Island",  group:"remaining", width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted", cert:"No cert" },
  { code:"SD", name:"South Dakota",  group:"remaining", width:"14'6\"", height:"14'6\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
  { code:"VA", name:"Virginia",      group:"remaining", width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted", cert:"No cert" },
  { code:"VT", name:"Vermont",       group:"remaining", width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted", cert:"No cert" },
  { code:"WV", name:"West Virginia", group:"remaining", width:"14'0\"", height:"14'0\"", ins:"$750K CSL", night:"w/ lighting",cert:"No cert" },
  { code:"NY", name:"New York",      group:"remaining", width:"16'0\"", height:"15'0\"", ins:"$1M CSL",   night:"Restricted", cert:"NYSDOT Required" },
  { code:"NJ", name:"New Jersey",    group:"remaining", width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Restricted", cert:"No cert" },
  { code:"NH", name:"New Hampshire", group:"remaining", width:"14'0\"", height:"13'6\"", ins:"$750K CSL", night:"Allowed",    cert:"No cert" },
  { code:"WA", name:"Washington",    group:"remaining", width:"14'0\"", height:"15'6\"", ins:"$750K CSL", night:"No Fri 3pm\u2013Mon 6am", cert:"WSDOT Permit" },
];

// ─── ADDITIONAL CHEAT-SHEET FIELDS ──────────────────────────────────────────
const LENGTH_BY_GROUP = { eight: "95'0\"", pevo: "75'0\"", standalone: "75'0\"", remaining: "75'0\"" };
const FEDERAL_WEIGHT = "80,000 lbs GVW";
const ROUTE_SURVEY_RULE = "Required over 16'0\" (W/H) or 150,000 lbs";
const DEFAULT_ESCORT = "~12' lead / 14' lead+rear (confirm w/ state DOT)";
const ESCORT_OVERRIDES = {
  CA: "Confirm width/weight triggers directly with Caltrans",
  TX: "14' front · 16' front+rear · 20' or 200k lbs adds DPS police escort",
  NY: "13'6\" width requires police escort — zero reciprocity",
  WA: "12' lead · 14' lead+rear · 16' or 200k lbs adds WSP escort",
  HI: "10' width is already near max — confirm escort needs with HDOT",
  DC: "Confirm with DDOT — federal overlay may add police coordination",
};
const STATE_DOT = {
  CA:"Caltrans", TX:"TxDOT", WA:"WSDOT", NY:"NYSDOT", FL:"FDOT",
  OH:"ODOT", GA:"GDOT", CO:"CDOT", AL:"ALDOT", AR:"ARDOT",
  AZ:"ADOT", ID:"ITD", IA:"IaDOT", KS:"KDOT", MO:"MoDOT",
  NE:"NDOT", NV:"NDOT", UT:"UDOT", CT:"ConnDOT", DE:"DelDOT",
  KY:"KYTC", LA:"LADOTD", MI:"MDOT", MS:"MDOT", NC:"NCDOT",
  NM:"NMDOT", SC:"SCDOT", OR:"ODOT", TN:"TDOT", WI:"WisDOT",
  WY:"WYDOT", AK:"ADOT", DC:"DDOT", HI:"HDOT", IL:"IDOT",
  IN:"INDOT", MD:"SHA", MA:"MassDOT", ME:"MaineDOT", MN:"MnDOT",
  MT:"MDT", ND:"NDDOT", OK:"ODOT", PA:"PennDOT", RI:"RIDOT",
  SD:"SDDOT", VA:"VDOT", VT:"VTrans", WV:"WVDOH", NJ:"NJDOT", NH:"NHDOT",
};

function extendedInfo(s) {
  return {
    length: LENGTH_BY_GROUP[s.group] || "75'0\"",
    weight: FEDERAL_WEIGHT,
    escort: ESCORT_OVERRIDES[s.code] || DEFAULT_ESCORT,
    survey: ROUTE_SURVEY_RULE,
    dot: STATE_DOT[s.code] || `${s.code}DOT`,
  };
}

const DEMO_ROUTE = ["CA", "NV", "AZ", "NM", "TX"];

function colorFor(C, key) {
  return { green: C.green, blue: C.blue, amber: C.amber, muted: C.muted }[key] || C.muted;
}

function ToggleSwitch({ on, onClick, onColor }) {
  return (
    <button
      onClick={onClick}
      role="switch"
      aria-checked={on}
      style={{
        width: 46, height: 26, borderRadius: 99, border: "none", cursor: "pointer",
        background: on ? onColor : "rgba(120,120,120,0.35)", position: "relative",
        transition: "background .2s", flexShrink: 0,
      }}
    >
      <span
        style={{
          position: "absolute", top: 3, left: on ? 23 : 3, width: 20, height: 20,
          borderRadius: "50%", background: "#fff", transition: "left .2s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
        }}
      />
    </button>
  );
}

function StateBookCover({ s, C, unlocked, onOpen }) {
  const meta = GROUP_META[s.group] || { label: "Unknown", color: "muted" };
  const groupColor = colorFor(C, meta.color);

  return (
    <div
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(); }}
      style={{
        background: C.surface, border: `1px solid ${C.border}`, borderTop: `3px solid ${groupColor}`,
        borderRadius: 14, height: 218, position: "relative", cursor: "pointer", overflow: "hidden",
        transition: "transform .15s, box-shadow .15s", display: "flex", flexDirection: "column",
        justifyContent: "space-between", padding: "16px 15px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = `0 14px 28px rgba(0,0,0,0.14), 0 0 0 1px ${groupColor}40`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "none";
        e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)";
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{
          width: 42, height: 42, borderRadius: 12, fontSize: 21, display: "flex", alignItems: "center", justifyContent: "center",
          background: `linear-gradient(135deg, ${groupColor}30, ${groupColor}12)`, border: `1px solid ${groupColor}40`,
        }}>
          {STATE_FLAGS[s.code] || "🇺🇸"}
        </div>
        {unlocked ? (
          <div style={{ background: `${groupColor}18`, color: groupColor, fontSize: 9, fontWeight: 800, padding: "3px 8px", borderRadius: 20, border: `1px solid ${groupColor}40` }}>
            {meta.label}
          </div>
        ) : (
          <div style={{
            background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 20, padding: "3px 8px",
            display: "flex", alignItems: "center", gap: 4,
          }}>
            <Lock size={10} color={C.textLight} />
            <span style={{ fontSize: 8.5, fontWeight: 800, color: C.textLight, letterSpacing: ".03em" }}>MEMBERS</span>
          </div>
        )}
      </div>

      <div>
        <div style={{ fontSize: 24, fontWeight: 900, color: C.text, lineHeight: 1 }}>{s.code}</div>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.textMid, marginTop: 3 }}>{s.name}</div>
      </div>

      {unlocked ? (
        <div style={{ display: "flex", gap: 6 }}>
          <div style={{ flex: 1, background: C.surfaceAlt, borderRadius: 7, padding: "6px 8px" }}>
            <div style={{ fontSize: 8.5, color: C.textLight, fontWeight: 700 }}>WIDTH</div>
            <div style={{ fontSize: 11.5, color: C.text, fontWeight: 800 }}>{s.width}</div>
          </div>
          <div style={{ flex: 1, background: C.surfaceAlt, borderRadius: 7, padding: "6px 8px" }}>
            <div style={{ fontSize: 8.5, color: C.textLight, fontWeight: 700 }}>INS.</div>
            <div style={{ fontSize: 11.5, color: C.text, fontWeight: 800 }}>{s.ins.replace(" CSL", "")}</div>
          </div>
        </div>
      ) : (
        <div style={{ position: "relative" }}>
          <div style={{ display: "flex", gap: 6, filter: "blur(3px)", userSelect: "none", opacity: 0.7 }} aria-hidden="true">
            <div style={{ flex: 1, background: C.surfaceAlt, borderRadius: 7, padding: "6px 8px" }}>
              <div style={{ fontSize: 8.5, color: C.textLight, fontWeight: 700 }}>WIDTH</div>
              <div style={{ fontSize: 11.5, color: C.text, fontWeight: 800 }}>{s.width}</div>
            </div>
            <div style={{ flex: 1, background: C.surfaceAlt, borderRadius: 7, padding: "6px 8px" }}>
              <div style={{ fontSize: 8.5, color: C.textLight, fontWeight: 700 }}>INS.</div>
              <div style={{ fontSize: 11.5, color: C.text, fontWeight: 800 }}>{s.ins.replace(" CSL", "")}</div>
            </div>
          </div>
          <div style={{
            position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 9.5, fontWeight: 800, color: C.gold,
          }}>
            Unlock to view
          </div>
        </div>
      )}

      <div style={{
        background: unlocked ? `${groupColor}12` : C.surfaceAlt, border: `1px solid ${unlocked ? groupColor + "40" : C.border}`,
        borderRadius: 8, padding: "7px 6px", textAlign: "center",
      }}>
        <span style={{ fontSize: 10.5, color: unlocked ? groupColor : C.textLight, fontWeight: 800 }}>
          {unlocked ? "View full guide →" : "Tap to unlock"}
        </span>
      </div>
    </div>
  );
}

export default function CheatSheetVault({ isLoggedIn = false, isVerified = false }) {
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(
    () => typeof window !== "undefined" && localStorage.getItem("theme") === "dark"
  );
  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDarkMode);
    localStorage.setItem("theme", isDarkMode ? "dark" : "light");
  }, [isDarkMode]);
  const C = isDarkMode ? DARK_C : LIGHT_C;

  const [previewVerified, setPreviewVerified] = useState(isVerified);
  const unlocked = isVerified || previewVerified;

  const [activeTab, setActiveTab] = useState("hud");
  const [alertsOn, setAlertsOn] = useState(true);
  const [routeIndex, setRouteIndex] = useState(0);
  const [alertBanner, setAlertBanner] = useState(null);
  const [selectedState, setSelectedState] = useState(null);

  const [query, setQuery] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");

  const currentGPSState = useMemo(
    () => STATES.find((s) => s.code === DEMO_ROUTE[routeIndex]) || STATES[0],
    [routeIndex]
  );

  function handleSimulateCrossing() {
    const nextIndex = (routeIndex + 1) % DEMO_ROUTE.length;
    setRouteIndex(nextIndex);
    const nextState = STATES.find((s) => s.code === DEMO_ROUTE[nextIndex]);
    if (alertsOn && nextState) {
      setAlertBanner(nextState);
      window.setTimeout(() => setAlertBanner((cur) => (cur?.code === nextState.code ? cur : cur)), 0);
    }
  }

  function openState(s) {
    if (!unlocked) {
      navigate(isLoggedIn ? "/verified" : "/signup");
      return;
    }
    setSelectedState(s);
    setAlertBanner(null);
  }

  const filteredStates = useMemo(() => {
    return STATES.filter((s) => {
      const matchesGroup = groupFilter === "all" || s.group === groupFilter;
      const q = query.trim().toLowerCase();
      return matchesGroup && (!q || s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q));
    });
  }, [query, groupFilter]);

  const filters = [
    ["all", "All States", C.navy],
    ["pevo", "14 PEVO", C.green],
    ["eight", "8-State", C.blue],
    ["standalone", "Stand-Alone", C.amber],
    ["remaining", "Remaining 21", C.muted],
  ];

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: C.page, minHeight: "100vh", color: C.text, transition: "background .3s" }}>
      <Navbar isDarkMode={isDarkMode} toggleTheme={() => setIsDarkMode((p) => !p)} />

      {/* Page header */}
      <div style={{ background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderBottom: `3px solid ${C.gold}`, padding: "20px 20px" }}>
        <div style={{ maxWidth: 1120, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: 11, background: C.gold, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🧭</div>
            <div>
              <h1 style={{ margin: 0, fontSize: 17, fontWeight: 900, color: "#fff" }}>50-State Cheat Sheet &amp; Road Companion</h1>
              <span style={{ fontSize: 11.5, color: C.gold, fontWeight: 600 }}>Live geofence alerts &middot; full state vault</span>
            </div>
          </div>

          {/* Tab switcher */}
          <div style={{ display: "flex", background: "rgba(255,255,255,0.08)", borderRadius: 10, padding: 4 }}>
            <button
              onClick={() => setActiveTab("hud")}
              style={{
                display: "flex", alignItems: "center", gap: 6, padding: "7px 13px", border: "none", borderRadius: 7,
                fontSize: 12, fontWeight: 700, cursor: "pointer",
                background: activeTab === "hud" ? C.gold : "transparent",
                color: activeTab === "hud" ? C.navy : "rgba(255,255,255,0.7)",
              }}
            >
              <Map size={13} /> HUD Mode
            </button>
            <button
              onClick={() => setActiveTab("vault")}
              style={{
                display: "flex", alignItems: "center", gap: 6, padding: "7px 13px", border: "none", borderRadius: 7,
                fontSize: 12, fontWeight: 700, cursor: "pointer",
                background: activeTab === "vault" ? C.gold : "transparent",
                color: activeTab === "vault" ? C.navy : "rgba(255,255,255,0.7)",
              }}
            >
              <BookOpen size={13} /> Browse All States
            </button>
          </div>
        </div>
      </div>

      {/* Why this matters — persistent educational note, both tabs */}
      <div style={{ maxWidth: 1120, margin: "16px auto 0", padding: "0 20px" }}>
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`, borderLeft: `4px solid ${C.gold}`,
          borderRadius: "0 12px 12px 0", padding: "14px 18px", display: "flex", gap: 12,
        }}>
          <Info size={16} color={C.gold} style={{ flexShrink: 0, marginTop: 2 }} />
          <p style={{ margin: 0, fontSize: 12, color: C.textSub, lineHeight: 1.7 }}>
            <strong style={{ color: C.text }}>Why this matters:</strong> pilot car requirements aren't uniform
            across the U.S. A load that's legal in Arizona can require a police escort in New York, and a width
            that needs no escort in Iowa can trigger one in Washington. Missing a state-specific rule isn't just
            a fine risk — it can mean an unsafe move for you, your carrier, and the public sharing the road. This
            cheat sheet distills each state's core numbers — width, height, insurance minimum, night-travel policy,
            and certification path — grouped by how that state actually regulates pilot cars. Use it to prep before
            a job or confirm a border crossing. It's a quick reference, not a substitute for checking with the
            state DOT directly — regulations change, and the final call is always theirs.
          </p>
        </div>
      </div>

      {/* Alert banner (slides in on simulated crossing, only if unlocked + alerts on) */}
      {alertBanner && (
        <div
          onClick={() => openState(alertBanner)}
          role="button"
          style={{
            maxWidth: 1120, margin: "16px auto 0", padding: "0 20px", cursor: "pointer",
          }}
        >
          <div style={{
            background: C.goldLight, border: `1.5px solid ${C.gold}`, borderRadius: 12,
            padding: "12px 16px", display: "flex", alignItems: "center", gap: 12,
          }}>
            <span style={{ fontSize: 22 }}>{STATE_FLAGS[alertBanner.code]}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: C.navy }}>
                Entering {alertBanner.name} &mdash; key limits just changed
              </div>
              <div style={{ fontSize: 11.5, color: C.textSub, marginTop: 2 }}>
                Max width {alertBanner.width} &middot; height {alertBanner.height} &middot; night travel: {alertBanner.night}.
                {" "}{unlocked ? "Tap for the full guide." : "Tap to unlock the full guide."}
              </div>
            </div>
            <ChevronRight size={16} color={C.gold} />
            <button
              onClick={(e) => { e.stopPropagation(); setAlertBanner(null); }}
              style={{ background: "none", border: "none", cursor: "pointer", color: C.textLight, padding: 4 }}
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ── HUD TAB ── */}
      {activeTab === "hud" && (
        <div style={{ maxWidth: 1120, margin: "0 auto", padding: 20 }}>

          {/* Geofence control card */}
          <div style={{
            background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "16px 18px",
            marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {alertsOn ? <Bell size={18} color={C.gold} /> : <BellOff size={18} color={C.textLight} />}
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text }}>Geofence Alerts</div>
                <div style={{ fontSize: 11.5, color: C.textLight, lineHeight: 1.5 }}>
                  {alertsOn
                    ? "You'll get notified automatically when regulations change as you cross a state line."
                    : "Alerts are off — browse the vault manually whenever you like."}
                </div>
              </div>
            </div>
            <ToggleSwitch on={alertsOn} onClick={() => setAlertsOn((p) => !p)} onColor={C.gold} />
          </div>

          {/* Current location HUD */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 22, boxShadow: "0 4px 12px rgba(0,0,0,0.06)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <Navigation2 size={13} color={C.green} />
              <span style={{ fontSize: 11.5, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: ".04em" }}>
                Current location (demo)
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 14, borderBottom: `1px solid ${C.border}`, paddingBottom: 18, marginBottom: 18 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ fontSize: 38, background: C.surfaceAlt, padding: 10, borderRadius: 14 }}>{STATE_FLAGS[currentGPSState.code]}</div>
                <div>
                  <div style={{ fontSize: 11.5, color: C.textLight }}>You're currently driving through</div>
                  <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: C.text }}>{currentGPSState.name} ({currentGPSState.code})</h2>
                </div>
              </div>
              <button
                onClick={() => openState(currentGPSState)}
                style={{
                  background: C.goldLight, border: `1px solid ${C.gold}`, color: C.gold, padding: "9px 16px",
                  borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", gap: 6,
                }}
              >
                {unlocked ? <BookOpen size={13} /> : <Lock size={13} />}
                {unlocked ? "View full state guide" : "Unlock full guide"}
              </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 18 }}>
              {[
                ["Max legal width", currentGPSState.width],
                ["High-pole threshold", currentGPSState.height],
                ["Max length", extendedInfo(currentGPSState).length],
                ["Night transit policy", currentGPSState.night],
                ["Escort trigger", extendedInfo(currentGPSState).escort],
              ].map(([label, val]) => (
                <div key={label} style={{ background: C.surfaceAlt, padding: 12, borderRadius: 8 }}>
                  <div style={{ fontSize: 10.5, color: C.textLight, fontWeight: 700 }}>{label.toUpperCase()}</div>
                  <div style={{ fontSize: 13.5, fontWeight: 800, color: C.text, marginTop: 3, lineHeight: 1.3 }}>{val}</div>
                </div>
              ))}
            </div>

            <button
              onClick={handleSimulateCrossing}
              style={{
                width: "100%", background: C.green, color: "#fff", border: "none", padding: "12px 16px",
                borderRadius: 10, fontWeight: 700, cursor: "pointer", fontSize: 13,
              }}
            >
              Simulate driving into next state (demo) &rarr; {DEMO_ROUTE[(routeIndex + 1) % DEMO_ROUTE.length]}
            </button>
            <p style={{ fontSize: 10.5, color: C.textLight, margin: "8px 0 0", lineHeight: 1.5 }}>
              This button simulates a real GPS border crossing for preview purposes. In production this fires
              automatically from live location + reverse geocoding.
            </p>
          </div>
        </div>
      )}

      {/* ── VAULT TAB ── */}
      {activeTab === "vault" && (
        <div style={{ maxWidth: 1120, margin: "0 auto", padding: 20 }}>

          {!unlocked && (
            <div style={{
              background: C.goldLight, border: `1px solid ${C.gold}`, borderRadius: 12, padding: "12px 16px",
              marginBottom: 18, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            }}>
              <ShieldCheck size={16} color={C.gold} />
              <span style={{ fontSize: 12.5, color: C.text, flex: 1 }}>
                Book covers below are visible to everyone — full details unlock once you're a verified member or driver.
              </span>
              <button
                onClick={() => navigate(isLoggedIn ? "/verified" : "/signup")}
                style={{ background: C.gold, color: C.navy, border: "none", padding: "7px 14px", borderRadius: 8, fontWeight: 800, fontSize: 12, cursor: "pointer" }}
              >
                {isLoggedIn ? "Get Verified" : "Join Free"}
              </button>
            </div>
          )}

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 18 }}>
            <div style={{ position: "relative", flex: "1 1 240px" }}>
              <Search size={14} color={C.textLight} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search state name or code..."
                style={{
                  width: "100%", padding: "10px 14px 10px 34px", borderRadius: 8, boxSizing: "border-box",
                  border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 13, outline: "none",
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {filters.map(([key, label, color]) => (
                <button
                  key={key}
                  onClick={() => setGroupFilter(key)}
                  style={{
                    padding: "8px 13px", borderRadius: 7, border: "none", cursor: "pointer",
                    background: groupFilter === key ? color : C.surfaceAlt,
                    color: groupFilter === key ? "#fff" : C.textSub,
                    fontWeight: groupFilter === key ? 700 : 500, fontSize: 11.5,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ fontSize: 12, color: C.textLight, marginBottom: 12 }}>
            Showing {filteredStates.length} of {STATES.length} states
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(175px, 1fr))", gap: 14 }}>
            {filteredStates.map((s) => (
              <StateBookCover key={s.code} s={s} C={C} unlocked={unlocked} onOpen={() => openState(s)} />
            ))}
          </div>

          {filteredStates.length === 0 && (
            <div style={{ textAlign: "center", padding: "48px 0", color: C.textLight, fontSize: 13 }}>
              No states match &ldquo;{query}&rdquo;.
            </div>
          )}
        </div>
      )}

      {/* Disclaimer */}
      <div style={{ maxWidth: 1120, margin: "0 auto", padding: "0 20px 32px" }}>
        <div style={{ padding: "12px 16px", borderRadius: 8, background: C.redLight, border: `1px solid ${C.red}22`, fontSize: 10.5, color: C.red, lineHeight: 1.6 }}>
          ⚠️ <strong>Disclaimer:</strong> This cheat sheet is for quick reference and pre-trip study only. Always
          verify current requirements with your state DOT permit authority before every permitted move.
        </div>
      </div>

      {/* Dev-only preview toggle — remove once real auth is wired in */}
      <div style={{ position: "fixed", bottom: 16, right: 16, zIndex: 500 }}>
        <button
          onClick={() => setPreviewVerified((p) => !p)}
          title="Dev preview only — simulates verified vs. guest state"
          style={{
            fontSize: 10.5, fontWeight: 700, padding: "8px 12px", borderRadius: 99,
            border: `1px solid ${C.border}`, background: C.surface, color: C.textSub, cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          Preview as: {unlocked ? "Verified" : "Guest"} (dev)
        </button>
      </div>

      {/* ── STATE DETAIL MODAL ── */}
      {selectedState && (
        <div
          onClick={() => setSelectedState(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(13,31,60,0.65)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, width: "100%", maxWidth: 560, overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,0.35)" }}
          >
            <div style={{ background: `linear-gradient(135deg, ${C.navy}, ${C.navyMid})`, padding: 20, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `3px solid ${C.gold}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 28 }}>{STATE_FLAGS[selectedState.code]}</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: "#fff" }}>{selectedState.name} ({selectedState.code})</h3>
                  <span style={{ fontSize: 10.5, color: C.gold, fontWeight: 700, textTransform: "uppercase" }}>
                    {GROUP_META[selectedState.group]?.label || "General"} &middot; {extendedInfo(selectedState).dot}
                  </span>
                </div>
              </div>
              <button onClick={() => setSelectedState(null)} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", width: 30, height: 30, borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={15} />
              </button>
            </div>

            <div style={{ padding: 20, maxHeight: "70vh", overflowY: "auto" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {[
                  ["Max legal width", selectedState.width],
                  ["High-pole threshold", selectedState.height],
                  ["Max length", extendedInfo(selectedState).length],
                  ["Max weight", extendedInfo(selectedState).weight],
                  ["Min insurance (CSL)", selectedState.ins],
                  ["Night transit policy", selectedState.night],
                ].map(([label, val]) => (
                  <div key={label} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, padding: 12, borderRadius: 10 }}>
                    <div style={{ fontSize: 10, color: C.textLight, fontWeight: 700 }}>{label.toUpperCase()}</div>
                    <div style={{ fontSize: 14, color: C.text, fontWeight: 800, marginTop: 3, lineHeight: 1.3 }}>{val}</div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 12, background: C.surfaceAlt, borderLeft: `4px solid ${C.gold}`, padding: 12, borderRadius: "0 8px 8px 0" }}>
                <div style={{ fontSize: 10, color: C.textLight, fontWeight: 700 }}>CERTIFICATION REQUIRED</div>
                <div style={{ fontSize: 14, color: C.text, fontWeight: 800, marginTop: 2 }}>{selectedState.cert}</div>
              </div>

              <div style={{ marginTop: 10, background: C.surfaceAlt, borderLeft: `4px solid ${C.blue}`, padding: 12, borderRadius: "0 8px 8px 0" }}>
                <div style={{ fontSize: 10, color: C.textLight, fontWeight: 700 }}>ESCORT TRIGGER</div>
                <div style={{ fontSize: 13, color: C.text, fontWeight: 700, marginTop: 2, lineHeight: 1.4 }}>{extendedInfo(selectedState).escort}</div>
              </div>

              <div style={{ marginTop: 10, background: C.surfaceAlt, borderLeft: `4px solid ${C.amber}`, padding: 12, borderRadius: "0 8px 8px 0" }}>
                <div style={{ fontSize: 10, color: C.textLight, fontWeight: 700 }}>ROUTE SURVEY</div>
                <div style={{ fontSize: 13, color: C.text, fontWeight: 700, marginTop: 2 }}>{extendedInfo(selectedState).survey}</div>
              </div>

              <p style={{ margin: "16px 0 0", fontSize: 10.5, color: C.red, opacity: 0.85, lineHeight: 1.5 }}>
                ⚠️ For quick pre-trip reference only. Always confirm current rules with {extendedInfo(selectedState).dot}
                before taking a job.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
